import { ReactNode, SyntheticEvent } from 'react';
import { Disableable, InputValue, Size, Status, Valued } from '@optimus-web/common';
import { Checkbox, CheckboxProps } from '../Checkbox';
import { FormLabel } from '../common/FormLabel';
import { FormMessage } from '../common/FormMessage';
import { FieldHeader } from '../Field/style';
import { Grid } from '../Grid';
import { GridProps } from '../Grid/types';
import { CheckboxesContainer, CheckboxGroupContainer, NestedGroupWrapper } from './style';

interface LeafCheckbox extends Valued {
    label: ReactNode;
    name: string;
}

interface GroupCheckbox extends LeafCheckbox {
    children: Options;
}

type Option = LeafCheckbox | GroupCheckbox;
type Options = Array<Option>;

export interface CheckboxGroupProps extends Disableable, GridProps {
    options: Options;
    label?: ReactNode;
    values?: Array<InputValue>;
    captionIconWithTooltip?: ReactNode;
    required?: boolean;
    onChange?: (event: SyntheticEvent<HTMLElement>, values: Array<InputValue>) => void;
    error?: ReactNode;
    size?: Exclude<Size, 'extraLarge'>;
}

type CheckboxGroupChangeHandler = (
    handledValues: Array<InputValue>
) => (event: SyntheticEvent<HTMLElement>, checked: boolean) => void;

interface GetCheckboxOptions {
    options: Options;
    values: Array<InputValue>;
    handleChange: CheckboxGroupChangeHandler;
    checkboxProps: Pick<CheckboxProps, 'disabled' | 'size'>;
}

const getAllChildrenValues = (children: Options): Array<InputValue> =>
    children.reduce((acc: Array<InputValue>, item: Option) => {
        if ('children' in item) {
            const res = getAllChildrenValues(item.children);
            return [...acc, ...res];
        }

        return [...acc, item.value];
    }, []);

const getCheckboxes = ({
    options,
    values,
    handleChange,
    checkboxProps,
}: GetCheckboxOptions): [ReactNode, number, number] => {
    let checkedCount = 0;
    let indeterminatedCount = 0;

    const result = options.map((option: Option) => {
        if ('children' in option) {
            const { name, value, label, children } = option;

            const [nestedResult, nestedCheckedCount, nestedIndeterminatedCount] = getCheckboxes({
                options: children,
                handleChange,
                values,
                checkboxProps,
            });

            const isChecked = nestedCheckedCount === children.length;
            const isIndeterminate =
                (nestedCheckedCount > 0 && nestedCheckedCount < children.length) || !!nestedIndeterminatedCount;

            if (isChecked) {
                checkedCount += 1;
            }
            if (isIndeterminate) {
                indeterminatedCount += 1;
            }

            return (
                <div key={value}>
                    <Checkbox
                        checked={isChecked}
                        indeterminate={isIndeterminate}
                        onChange={handleChange(getAllChildrenValues(children))}
                        data-test-id={`${name}-${value}`}
                        name={name}
                        value={value}
                        {...checkboxProps}
                    >
                        {label}
                    </Checkbox>
                    <NestedGroupWrapper>{nestedResult}</NestedGroupWrapper>
                </div>
            );
        }

        const { value, label, name } = option;

        const isChecked = values.includes(value);

        if (isChecked) {
            checkedCount += 1;
        }

        return (
            <Checkbox
                key={value}
                name={name}
                value={value}
                checked={isChecked}
                onChange={handleChange([value])}
                data-test-id={`${name}-${value}`}
                {...checkboxProps}
            >
                {label}
            </Checkbox>
        );
    });

    return [result, checkedCount, indeterminatedCount];
};

export const CheckboxGroup = ({
    options,
    label,
    values = [],
    onChange,
    error,
    disabled,
    size,
    captionIconWithTooltip,
    required,
    ...other
}: CheckboxGroupProps) => {
    const handleChange: CheckboxGroupChangeHandler = (handledValues) => (event, checked) => {
        if (onChange) {
            if (checked) {
                onChange(event, [...values, ...handledValues]);
            } else {
                onChange(
                    event,
                    values.filter((item) => !handledValues.includes(item))
                );
            }
        }
    };

    const [result] = getCheckboxes({ options, values, handleChange, checkboxProps: { disabled, size } });

    return (
        <CheckboxGroupContainer>
            {label ? (
                <FieldHeader>
                    <FormLabel required={required} as="legend">
                        {label}
                    </FormLabel>
                    {captionIconWithTooltip}
                </FieldHeader>
            ) : null}
            <CheckboxesContainer>
                <Grid rowGap="0" columnGap="300" {...other}>
                    {result}
                </Grid>
            </CheckboxesContainer>
            {error ? <FormMessage status={Status.Error}>{error}</FormMessage> : null}
        </CheckboxGroupContainer>
    );
};
